
import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Store', path: '/shop', hasDropdown: true },
    { name: 'Workshops', path: '/workshops' },
    { name: 'Speaking', path: '/speaking' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const storeCategories = [
    { name: 'All Products', path: '/shop', icon: 'ri-store-line' },
    { name: 'Books', path: '/shop?type=book', icon: 'ri-book-line' },
    { name: 'Apparel', path: '/shop?type=apparel', icon: 'ri-shirt-line' },
    { name: 'Digital Products', path: '/shop?type=digital', icon: 'ri-download-line' }
  ];

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsStoreDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsStoreDropdownOpen(false);
    }, 150);
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsStoreDropdownOpen(!isStoreDropdownOpen);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="text-2xl font-bold text-slate-800">
              Elbert Alexander
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer ${
                isActive('/') ? 'text-slate-800 border-b-2 border-blue-600' : ''
              }`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer ${
                isActive('/about') ? 'text-slate-800 border-b-2 border-blue-600' : ''
              }`}
            >
              About
            </Link>
            
            {/* Store Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={handleDropdownClick}
                className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer flex items-center whitespace-nowrap ${
                  location.pathname === '/shop' ? 'text-slate-800 border-b-2 border-blue-600' : ''
                }`}
              >
                Store
                <i className={`ri-arrow-down-s-line ml-1 text-sm transition-transform ${
                  isStoreDropdownOpen ? 'rotate-180' : ''
                }`}></i>
              </button>
              
              {isStoreDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {storeCategories.map(category => (
                    <Link
                      key={category.name}
                      to={category.path}
                      className="flex items-center px-4 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap"
                      onClick={() => setIsStoreDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-full mr-3">
                        <i className={`${category.icon} text-blue-800`}></i>
                      </div>
                      <span className="font-medium">{category.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link 
              to="/workshops" 
              className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer ${
                isActive('/workshops') ? 'text-slate-800 border-b-2 border-blue-600' : ''
              }`}
            >
              Workshops
            </Link>
            <Link 
              to="/speaking" 
              className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer ${
                isActive('/speaking') ? 'text-slate-800 border-b-2 border-blue-600' : ''
              }`}
            >
              Speaking
            </Link>
            <Link 
              to="/blog" 
              className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer ${
                isActive('/blog') ? 'text-slate-800 border-b-2 border-blue-600' : ''
              }`}
            >
              Insights
            </Link>
            <Link 
              to="/contact" 
              className={`text-slate-600 hover:text-slate-800 font-medium cursor-pointer ${
                isActive('/contact') ? 'text-slate-800 border-b-2 border-blue-600' : ''
              }`}
            >
              Contact
            </Link>
            <Link to="/workshops">
              <Button size="sm" className="bg-blue-800 hover:bg-blue-900 text-white whitespace-nowrap">
                Book Workshop
              </Button>
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-slate-600 hover:text-slate-800 cursor-pointer"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              
              {/* Mobile Store Section */}
              <div className="space-y-2">
                <Link 
                  to="/shop" 
                  className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer block"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Store
                </Link>
                <div className="pl-4 space-y-2">
                  {storeCategories.map(category => (
                    <Link
                      key={category.name}
                      to={category.path}
                      className="flex items-center text-slate-500 hover:text-slate-700 text-sm cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className={`${category.icon} mr-2 text-blue-800`}></i>
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              <Link 
                to="/workshops" 
                className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                Workshops
              </Link>
              <Link 
                to="/speaking" 
                className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                Speaking
              </Link>
              <Link 
                to="/blog" 
                className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                Insights
              </Link>
              <Link 
                to="/contact" 
                className="text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              <Link to="/workshops" onClick={() => setIsMenuOpen(false)}>
                <Button size="sm" className="bg-blue-800 hover:bg-blue-900 text-white w-fit whitespace-nowrap">
                  Book Workshop
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
